import { AbstractBlock } from '../AbstractBlock';
import { ShapeSettings } from '../setting/ShapeSettings';
import { ColorSettings } from '../setting/ColorSettings';
import { CodeSetting } from '../setting/CodeSetting';
import { GlobalImport } from '../../utils/GlobalImport';
import { BlockType } from '../../@types/BlockType';
import { CameraCodeSetting } from '../setting/CameraCodeSetting';
import { block_translation } from '../../lang/BlockTranslation';

const Blockly = GlobalImport.getBlockly();
const t = block_translation;
export class CameraElement_v2_1 extends AbstractBlock {
  constructor(name: string) {
    super(name, t.to('カメラ', 'camera'));
  }
  defineBlock(block: BlockType): void {
    ShapeSettings.setElementShape(block);
    ColorSettings.setObjectColor(block);

    ShapeSettings.setTitleField(block, this.title);

    block.appendDummyInput().appendField(t.to('位置', 'position') + ':');
    ShapeSettings.setVR3Fields(block, 'position', 0, 1.7, 4);

    const click_type = new Blockly.FieldDropdown([
      [t.to('マウスかタップ', 'mouse_or_tap'), 'mouse'],
      [t.to('注目点', 'focus'), 'cursor'],
      [t.to('注目してマウスかタップ', 'focus_mouse_or_tap'), 'cursor_mouse'],
    ]);

    block
      .appendDummyInput()
      .appendField(t.to('クリックの方法', 'click_type'))
      .appendField(click_type, 'click_type');

    ShapeSettings.addAttributeInput(block);
  }

  generateAssetsHTMLCode(): string {
    return `
    <a-asset-item id="avator1"  src="../public/avator/teat_Avatar_ver2.glb" crossorigin="anonymous" response-type="arraybuffer"></a-asset-item>
    <a-asset-item id="avator2"  src="../public/avator/teat_Avatar_ver3.glb" crossorigin="anonymous" response-type="arraybuffer"></a-asset-item>
    <a-asset-item id="avator3"  src="../public/avator/teat_Avatar_ver4.glb" crossorigin="anonymous" response-type="arraybuffer"></a-asset-item>
      
      <!-- リグ空っぽでもエンティティは必要 -->
      <template id="rig-template">
        <a-entity>
        </a-entity>
      </template>
      
      <template id="head-template" >
        <a-entity>
          <a-text class="name-tag" font="/public/custom-msdf/custom-msdf.json" negate="false"
             position="0 1.7 0.5" rotation="0 180 0" scale="2 1 1" text="value:Hello World;align:center;side:double" ></a-text>
          <a-gltf-model class="avator-model" position="0 -2 0.5" rotation="0 180 0" scale="1 1 1" src="" animation-mixer="" networked-audio-source></a-gltf-model>
        </a-entity>
      </template>
      `;
  }

  generateAfterbodyJavaScriptCode(): string {
    return `
      NAF.schemas.add({
        template: '#rig-template',
        components: [
          'position',
        ],
      });
      NAF.schemas.add({
        template: '#head-template',
        components: [
          'rotation',
          {
            selector: '.name-tag',
            component: 'text',
            property: 'value',
          },
          {
            selector: '.avator-model',
            component: 'gltf-model',
          },
        ],
      });

    `;
  }

  getRigCode(block: BlockType, cameraSubBlocks: string): string {
    const colorDefCode = `
    `;

    // Cameraを運ぶrigの役目を持つentity wasdを制御し、位置情報を共有する
    let rigAtt = CodeSetting.getVR3FieldValues(block, 'position');
    rigAtt += CodeSetting.getAttributeCode('opacity', '0');
    rigAtt += CodeSetting.getAttributeCode('movement-controls', '');
    rigAtt += CodeSetting.getAttributeCode('id', 'rig');

    rigAtt += CodeSetting.getAttributeCode('wall-collider__f', 'raycaster:#raycaster-f');
    rigAtt += CodeSetting.getAttributeCode('wall-collider__b', 'raycaster:#raycaster-b');
    rigAtt += CodeSetting.getAttributeCode('wall-collider__r', 'raycaster:#raycaster-r');
    rigAtt += CodeSetting.getAttributeCode('wall-collider__l', 'raycaster:#raycaster-l');
    rigAtt += CodeSetting.getAttributeCode('stand-on', 'raycaster:#stand-on-ray');

    // NAFを使って位置を同期
    rigAtt += CodeSetting.getAttributeCode(
      'networked',
      'template:#rig-template;attachTemplateToLocal:true;'
    );
    const str =
      colorDefCode + CodeSetting.getElementCode('a-entity', rigAtt, block.id, cameraSubBlocks);

    return str;
  }

  codeGenerateCore(block: BlockType): string {
    const click_type = CodeSetting.getFieldValue(block, 'click_type');

    const additional_attribute = CodeSetting.getSubAttributeCode(block);
    let attribute = '';
    attribute += CodeSetting.getAttributeCode('look-controls', 'pointerLockEnabled: false');
    attribute += CodeSetting.getAttributeCode('id', 'main-camera');
    attribute += CodeSetting.getAttributeCode('camera', '');
    attribute += CodeSetting.getAttributeCode('networked', 'template:#head-template;');
    // attribute += CodeSetting.getAttributeCode('spawn-in-circle', 'radius:3');

    if (click_type === 'cursor_mouse') {
      attribute += CodeSetting.getAttributeCode('mouse-cursor', '');
    }

    // カメラのサブブロックを取得
    let subBlocks = '';
    const cursorCode = CameraCodeSetting.getCameraCursorCode(click_type, block.id);

    // 地面をなぞるためのレイキャスター
    let standOnAtt = '';
    standOnAtt += CodeSetting.getAttributeCode('id', 'stand-on-ray');
    standOnAtt += CodeSetting.getAttributeCode(
      'raycaster',
      'objects: .collidable;  far: 10; autoRefresh:true; direction:0 -1 0'
    );
    const standOnRay = CodeSetting.getElementCode('a-entity', standOnAtt, block.id, '');

    // 四方のレイキャスターを定義
    let frontRayAtt = '';
    frontRayAtt += CodeSetting.getAttributeCode('id', 'raycaster-f');
    frontRayAtt += CodeSetting.getAttributeCode(
      'raycaster',
      'objects: .collidable; far: 1; direction:0 0 -1'
    );
    const frontRay = CodeSetting.getElementCode('a-entity', frontRayAtt, block.id, '');

    let backRayAtt = '';
    backRayAtt += CodeSetting.getAttributeCode('id', 'raycaster-b');
    backRayAtt += CodeSetting.getAttributeCode(
      'raycaster',
      'objects: .collidable; far: 1; direction:0 0 1'
    );
    const backRay = CodeSetting.getElementCode('a-entity', backRayAtt, block.id, '');

    let leftRayAtt = '';
    leftRayAtt += CodeSetting.getAttributeCode('id', 'raycaster-l');
    leftRayAtt += CodeSetting.getAttributeCode(
      'raycaster',
      'objects: .collidable; far: 1; direction:1 0 0'
    );
    const leftRay = CodeSetting.getElementCode('a-entity', leftRayAtt, block.id, '');

    let rightRayAtt = '';
    rightRayAtt += CodeSetting.getAttributeCode('id', 'raycaster-r');
    rightRayAtt += CodeSetting.getAttributeCode(
      'raycaster',
      'objects: .collidable; far: 1; direction:-1 0 0'
    );
    const rightRay = CodeSetting.getElementCode('a-entity', rightRayAtt, block.id, '');

    subBlocks += standOnRay + frontRay + backRay + leftRay + rightRay;
    const camera = CodeSetting.getElementCode(
      'a-entity',
      attribute + additional_attribute,
      block.id,
      cursorCode
    );

    // アバターを運ぶリグの設定
    let str = this.getRigCode(block, camera + subBlocks);
    if (click_type === 'cursor') {
      str += `
			let left = document.createElement("a-entity");
			let right = document.createElement("a-entity");

			// hand controler
			left.id = "ctlL";
			right.id = "ctlR";

			left.setAttribute("laser-controls","hand: left");		
			right.setAttribute("laser-controls","hand: right");
			parent.appendChild(left);
			parent.appendChild(right);
			`;
    }
    return str;
  }
}
